import React from 'react';
import { Chip, Paper, Stack } from '@mui/material';
import { AccountCircle, NotificationsActive, Schedule, PersonOutline } from '@mui/icons-material';
import { useMsal, useAccount } from '@azure/msal-react';
import { ApprovalRequest } from '../../../services/ApprovalService';
import Field from './Field';

interface ApprovalSummaryProps {
  approval: ApprovalRequest;
}

const ApprovalSummary: React.FC<ApprovalSummaryProps> = ({ approval }) => {
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] ?? {});
  const currentEmail = account?.username ?? '';

  const isBackupApprover = !!(
    currentEmail &&
    approval.backupApproverEmail &&
    currentEmail.toLowerCase() === approval.backupApproverEmail.toLowerCase()
  );

  return (
    <Paper
      elevation={0}
      sx={{ p: 2, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1.5 }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} flexWrap="wrap">
        <Field icon={<AccountCircle />} label="Approver">
          {approval.approverEmail || '(unassigned)'}
        </Field>
        {approval.backupApproverEmail && (
          <Field icon={<PersonOutline />} label="Backup Approver">
            {approval.backupApproverEmail}
            {isBackupApprover && (
              <Chip size="small" label="You" color="info" sx={{ ml: 1 }} />
            )}
          </Field>
        )}
        <Field icon={<Schedule />} label="Requested">
          {new Date(approval.requestedAt).toLocaleString()}
        </Field>
        {approval.approvalTimeout && (
          <Field icon={<Schedule />} label="Timeout">
            {new Date(approval.approvalTimeout).toLocaleString()}
          </Field>
        )}
        {approval.isPending && approval.nextReminderAt && (
          <Field icon={<NotificationsActive />} label="Next reminder">
            {new Date(approval.nextReminderAt).toLocaleString()}
            {approval.reminderCount > 0 && ` (sent ${approval.reminderCount})`}
          </Field>
        )}
        {!approval.isPending && approval.reminderCount > 0 && (
          <Field icon={<NotificationsActive />} label="Reminders sent">
            {approval.reminderCount}
          </Field>
        )}
        <Field label="Workflow Instance">
          <Chip size="small" label={approval.workflowInstanceId} sx={{ fontFamily: 'monospace' }} />
        </Field>
      </Stack>
    </Paper>
  );
};

export default ApprovalSummary;
